import React, { useState } from 'react';
import { Form, Input, Checkbox, Radio, Button, message, notification, Typography } from 'antd';
import { PhoneOutlined } from '@ant-design/icons';
import { useMemberService } from '../../services/members/MemberServiceProvider';
import { useClient } from '../../common/ClientProvider';
import { useClientConfig } from '../../common/client/ClientConfigProvider';
import { CreateMemberRequest } from '../../requests/member/CreateMemberRequest';
import HealthFormSubmissionRequest from '../../requests/member/health/HealthFormSubmissionRequest';
import { GuestType } from '../../model/members/GuestType';
import { DATE_OF_BIRTH_NOT_PROVIDED } from './profile/v2/completion';
import {
    DEFAULT_SIGNUP_HOW_THEY_HEARD,
    DEFAULT_SIGNUP_INTERESTS,
    isReferralHowTheyHeard,
    SignupOption,
} from '../../model/client/ClientSettings';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import MemberSearchBar from '../common/MemberSearchBar';
import { useMember } from '../../common/member/MemberContext';
import { MemberPermissions } from '../../model/auth/Permissions';
import { phoneNumberRule } from '../../utils/phone';

interface EnquiryFormValues {
    fullName: string;
    email: string;
    phoneNumber: string;
    interests: string[];
    guestType?: GuestType;
    howTheyHeard?: string;
    referringMemberId?: string;
    referringMemberName?: string;
    companyName?: string;
    notes?: string;
}

interface EnquiryFormProps {
    /**
     * Which guest-type options the operator can pick from (order preserved).
     * Resolved by the caller from per-client config (guest vs enquiry list);
     * each value is a GuestType enum name, the label is what's shown.
     */
    guestTypeOptions: SignupOption[];
    /** Label for the guest-type field. Defaults to "Guest Type". */
    guestTypeLabel?: string;
    /** Submit button text. Defaults to "Create Guest". */
    submitLabel?: string;
    /**
     * Where to navigate after a successful create. When omitted the form simply
     * resets and stays put (e.g. so a tour operator can move to the next tab).
     */
    redirectOnSuccess?: string;
    /** Success toast text. Defaults to "Guest signup completed successfully". */
    successMessage?: string;
    /** data-testid prefix so callers can distinguish guest vs tour forms. */
    testIdPrefix?: string;
}

// LeadSource by guest type. Fitness/Squash are walk-in visitors; Tour and Call
// are captured by the tour operator and each carry their own source so the
// retention screen can segment them. Anything unmapped falls back to 'Visitor'.
const LEAD_SOURCE_BY_GUEST_TYPE: Partial<Record<GuestType, string>> = {
    Tour: 'Tour',
    Call: 'Call',
};

const EnquiryForm: React.FC<EnquiryFormProps> = ({
    guestTypeOptions,
    guestTypeLabel = 'Guest Type',
    submitLabel = 'Create Guest',
    redirectOnSuccess,
    successMessage = 'Guest signup completed successfully',
    testIdPrefix = 'guest-signup',
}) => {
    const [form] = Form.useForm<EnquiryFormValues>();
    const memberService = useMemberService();
    const { currentClientId } = useClient();
    const { signup } = useClientConfig();
    const collectCompanyName = signup?.collectCompanyName ?? false;
    // Per-client option lists, falling back to the built-in defaults if a stale
    // config response is missing them.
    const interestOptions = signup?.interests ?? DEFAULT_SIGNUP_INTERESTS;
    const howTheyHeardOptions = signup?.howTheyHeard ?? DEFAULT_SIGNUP_HOW_THEY_HEARD;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const { permissions, isAdmin } = useMember();

    const hasPermission = (name: string) =>
        permissions?.permissions?.some((p) => p.name === name);

    const canSeeNotes = isAdmin || hasPermission(MemberPermissions.TourRegistration);

    const buildHealthFormRequest = (): HealthFormSubmissionRequest => {
        return {
            answers: [],
        };
    };

    const buildCreateMemberRequest = (values: EnquiryFormValues): CreateMemberRequest => {
        const today = dayjs();
        const validFrom = today.format('YYYY-MM-DD');
        const validTo = today.add(1, 'month').format('YYYY-MM-DD');

        const healthForm = buildHealthFormRequest();

        let referringMember = undefined;
        if (isReferralHowTheyHeard(values.howTheyHeard)) {
            if (values.referringMemberId) {
                referringMember = values.referringMemberId;
            } else if (values.referringMemberName) {
                referringMember = `NotFound: ${values.referringMemberName}`;
            }
        }

        // howTheyHeard now carries the stored code directly (the config option's
        // value, e.g. "WebsiteGoogle"), so no normalisation is needed — it already
        // matches the codes used in EditFollowupModal and the retention screen.
        const normalisedHowTheyHeard = values.howTheyHeard || undefined;

        const guestType = values.guestType;
        const leadSource = (guestType && LEAD_SOURCE_BY_GUEST_TYPE[guestType]) ?? 'Visitor';
        // Tour and Call are tour-operator enquiries; both seed a follow-up
        // (creationType Tour). Fitness/Squash walk-ins are plain guests.
        const isTourEnquiry = guestType === 'Tour' || guestType === 'Call';

        const metadata: Record<string, any> = {
            Interests: values.interests,
            // Send as a plain enum string. Previously wrapped in an array which
            // the backend stringified to "[Fitness]", "[Squash]", "[Tour]".
            ...(guestType ? { GuestType: guestType } : {}),
            LeadSource: leadSource,
            // Only a booked Tour automatically starts at the "Tour Booked"
            // pipeline stage; Call/Fitness/Squash are left for manual triage in
            // the retention screen.
            ...(guestType === 'Tour' ? { LeadType: 'TourBooked' } : {}),
            ...(normalisedHowTheyHeard ? { HowTheyHeard: normalisedHowTheyHeard } : {}),
            ...(referringMember ? { ReferringMember: referringMember } : {}),
            // Gated by signup.collectCompanyName; only persisted when the flag is
            // on and a value was entered.
            ...(collectCompanyName && values.companyName?.trim()
                ? { CompanyName: values.companyName.trim() }
                : {}),
        };

        const request: CreateMemberRequest = {
            name: values.fullName,
            username: values.email,
            // The guest form doesn't ask for a date of birth or an address, so it must
            // not invent them. It used to send a DOB of exactly 18 years ago and fill
            // the address with "To be Completed" — both read as real answers, so when
            // the guest later logged in to join, the profile-completion prompt saw a
            // complete address and a valid DOB and never asked for either.
            // Blank address fields are what self-service onboarding already sends, and
            // the DOB is the explicit not-provided sentinel (DateTime.MinValue) rather
            // than a dropped key, so the payload states that no date was collected
            // instead of leaving the server to infer it from an absent field. NOT null
            // — that would fail to bind to the non-nullable DateOfBirth and 400 it.
            dateOfBirth: DATE_OF_BIRTH_NOT_PROVIDED,
            emailAddress: values.email,
            addressLine1: '',
            addressLine2: '',
            townCity: '',
            countyState: '',
            postZipCode: '',
            country: 'United Kingdom',
            phoneNumber: values.phoneNumber,
            receiveEmails: false,
            status: 'Inactive',
            // A guest/enquiry has not chosen a plan, so we send NO category and let the
            // server assign the hidden "Default New Member" placeholder (MemberService
            // .GetDefaultNewMemberCategory, seeded per client by V44) — the same path
            // self-service onboarding and admin quick-create take. Previously this
            // guessed a category whose name contained "guest" and, failing that, took
            // whichever category happened to be first in the list, which is how guests
            // ended up on real priced plans like "Student Annual".
            membershipCategory: null,
            clientName: currentClientId,
            healthForm,
            // No direct debit is set up at guest signup. A guest/enquiry has given us
            // no bank details, so we must NOT write a placeholder mandate ("Guest
            // Member" / all-zero sort code and account number) — the backend skips
            // ProcessDirectDebitSetup entirely when this is null, and DD is collected
            // later if the guest converts to a member.
            directDebitRequest: null,
            membershipPaymentRequest: null,
            validFrom,
            validTo,
            isEnabled: false,
            creationType: isTourEnquiry ? 'Tour' : 'Guest',
            metadata,
            notes: values.notes || '',
        };

        return request;
    };

    const handleSubmit = async (values: EnquiryFormValues) => {
        try {
            setLoading(true);
            const request = buildCreateMemberRequest(values);
            await memberService.create(request);
            message.success(successMessage);
            form.resetFields();
            if (redirectOnSuccess) {
                navigate(redirectOnSuccess);
            }
        } catch (error) {
            console.error('Error creating guest member', error);
            if (error instanceof Error) {
                notification.error({
                    message: 'Error',
                    description: error.message || 'An error occurred while signing up the guest',
                });
            } else {
                notification.error({
                    message: 'Error',
                    description: 'An unknown error occurred while signing up the guest',
                });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
                interests: [],
            }}
        >
            <Form.Item
                name="fullName"
                label="Name"
                rules={[{ required: true, message: 'Please enter the guest name' }]}
            >
                <Input placeholder="Full name" />
            </Form.Item>

            <Form.Item
                name="email"
                label="Email"
                rules={[
                    { required: true, message: 'Please enter an email address' },
                    { type: 'email', message: 'Please enter a valid email address' },
                ]}
            >
                <Input
                    type="email"
                    placeholder="guest@example.com"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                />
            </Form.Item>

            <Form.Item
                name="phoneNumber"
                label="Phone Number"
                rules={[
                    { required: true, message: 'Please enter a phone number' },
                    phoneNumberRule(),
                ]}
            >
                <Input type="tel" placeholder="07..." prefix={<PhoneOutlined />} />
            </Form.Item>

            {collectCompanyName && (
                <Form.Item name="companyName" label="Company" data-testid={`${testIdPrefix}-company`}>
                    <Input placeholder="Company name" data-testid={`${testIdPrefix}-company-input`} />
                </Form.Item>
            )}

            <Form.Item
                name="interests"
                label="Interests"
                rules={[
                    {
                        validator: (_, value) => {
                            if (value && Array.isArray(value) && value.length > 0) {
                                return Promise.resolve();
                            }
                            return Promise.reject(new Error('Please select at least one interest'));
                        },
                    },
                ]}
            >
                <Checkbox.Group>
                    {interestOptions.map((option) => (
                        <Checkbox key={option.value} value={option.value}>{option.label}</Checkbox>
                    ))}
                </Checkbox.Group>
            </Form.Item>

            <Form.Item
                name="howTheyHeard"
                label="How They Heard About Us"
            >
                <Radio.Group>
                    {howTheyHeardOptions.map((option) => (
                        <Radio key={option.value} value={option.value}>{option.label}</Radio>
                    ))}
                </Radio.Group>
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.howTheyHeard !== currentValues.howTheyHeard}>
                {({ getFieldValue }) =>
                    isReferralHowTheyHeard(getFieldValue('howTheyHeard')) ? (
                        <div style={{ marginBottom: 24, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
                            <Typography.Text strong style={{ display: 'block', marginBottom: 16 }}>
                                Referring Member Details
                            </Typography.Text>

                            <Form.Item label="Search Existing Member">
                                <MemberSearchBar
                                    onSelect={(member) => form.setFieldValue('referringMemberId', member.membershipId)}
                                    onRefresh={() => {}}
                                />
                                <div style={{ marginTop: 8, fontSize: '12px', color: '#888' }}>
                                    Select a member above. To enter manually, use the field below.
                                </div>
                            </Form.Item>

                            <Form.Item name="referringMemberId" hidden>
                                <Input />
                            </Form.Item>

                            <div style={{ textAlign: 'center', margin: '16px 0', color: '#999' }}>- OR -</div>

                            <Form.Item name="referringMemberName" label="Member Name (If not found in search)">
                                <Input placeholder="Enter member name manually" />
                            </Form.Item>
                        </div>
                    ) : null
                }
            </Form.Item>

            <Form.Item
                name="guestType"
                label={guestTypeLabel}
                rules={[{ required: true, message: 'Please select a guest type' }]}
            >
                <Radio.Group data-testid={`${testIdPrefix}-guest-type`}>
                    {guestTypeOptions.map((option) => (
                        <Radio key={option.value} value={option.value}>{option.label}</Radio>
                    ))}
                </Radio.Group>
            </Form.Item>

            {canSeeNotes && (
                <Form.Item
                    name="notes"
                    label="Notes"
                    data-testid={`${testIdPrefix}-notes`}
                >
                    <Input.TextArea
                        rows={4}
                        placeholder="Add any notes about this guest..."
                        data-testid={`${testIdPrefix}-notes-input`}
                    />
                </Form.Item>
            )}

            <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} block data-testid={`${testIdPrefix}-submit`}>
                    {submitLabel}
                </Button>
            </Form.Item>
        </Form>
    );
};

export default EnquiryForm;
